import { type AxiosError } from "axios";

interface ValidationErrorItem {
  loc?: Array<string | number>;
  // Our 422 handler emits `message`; raw FastAPI validation errors emit `msg`.
  message?: string;
  msg?: string;
}

export interface ApiErrorResponse {
  detail: string | ValidationErrorItem[];
  error_type?: string;
}

// FastAPI validation errors (422) arrive as a list of {loc, message} objects.
// Flatten them into something readable like
// "description: String should have at most 300 characters".
export const formatErrorDetail = (
  detail: ApiErrorResponse["detail"] | undefined
): string | undefined => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        const message = item?.message ?? item?.msg;
        if (!message) return null;
        const field = Array.isArray(item.loc)
          ? item.loc.filter((part) => part !== "body").pop()
          : null;
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean);
    if (messages.length) return messages.join("; ");
  }
  return undefined;
};

export default function handleApiError(
  response: AxiosError,
  onError: (error: string, errorType?: string) => void
): void {
  if (
    response?.status === 400 ||
    response?.status === 401 ||
    response?.status === 403 ||
    response?.status === 422
  ) {
    // @ts-expect-error
    const body: ApiErrorResponse | undefined = response?.body;
    const errorMessage =
      formatErrorDetail(body?.detail) || "Something went wrong";
    const errorType = body?.error_type;
    onError(errorMessage, errorType);
  } else {
    onError("Something went wrong");
  }
}
